import {
  Context,
  createContext,
  useContext,
  useContextSelector,
} from 'use-context-selector';
import { IRaterContextState, RaterTab } from '../types';
import {
  RaterReducerActions,
  RaterReducerDispatch,
  RaterReducerState,
} from './rater.reducer';

import { IUnderwritingSubCoverage } from '@converge/graphql';
import { formatList } from '@converge/utils';
import { noCase } from 'change-case';
import pluralize from 'pluralize';

export const RaterDispatchContext = createContext<RaterReducerDispatch | null>(
  null
);

export const RaterStateContext = createContext<RaterReducerState | null>(null);

export const RaterActionsContext =
  createContext<typeof RaterReducerActions>(RaterReducerActions);

export const useRaterStateContext = () => {
  const context = useContext(RaterStateContext);

  if (!context) {
    throw new Error('RaterStateContext not found');
  }

  return context;
};

export const useRaterStateContextSelector = <Selected,>(
  selector: (value: IRaterContextState) => Selected
) => {
  return useContextSelector(
    RaterStateContext as Context<IRaterContextState>,
    selector
  );
};

export const useRaterUnsavedCounts = () => {
  return useRaterStateContextSelector(
    ({
      quote: {
        coverages,
        endorsements,
        subjectivities,
        excessTowerLayers,
        hasChanged,
        enterpriseRatings,
        scheduleRatings,
      },
      submission,
      changedFields,
    }) => {
      const resources = {
        fields: changedFields.length,
        coverages: coverages.filter(({ hasChanged }) => hasChanged).length,
        subCoverages: coverages.reduce(
          (acc, coverage) =>
            acc +
            coverage.subCoverages.filter(({ hasChanged }) => hasChanged).length,
          0
        ),
        endorsements: endorsements.filter(({ hasChanged }) => hasChanged)
          .length,
        subjectivities: subjectivities.filter(({ hasChanged }) => hasChanged)
          .length,
        excessTowerLayers: excessTowerLayers.filter(
          ({ hasChanged }) => hasChanged
        ).length,
        excessTowerLayerParticipants: excessTowerLayers.reduce(
          (acc, layer) =>
            acc +
            layer.participants.filter(({ hasChanged }) => hasChanged).length,
          0
        ),
        enterpriseRatings: enterpriseRatings.filter(
          ({ hasChanged }) => hasChanged
        ).length,
        scheduleRatings: scheduleRatings.filter(({ hasChanged }) => hasChanged)
          .length,
      };

      const asEntries = Object.entries(resources)
        .sort(([, countA], [, countB]) => countB - countA)
        .map(
          ([key, value]) =>
            [
              key,
              { count: value, text: pluralize(noCase(key), value, true) },
            ] as [keyof typeof resources, { count: number; text: string }]
        );

      const asText = asEntries.map(([, { text }]) => text).join(', ');

      const asTextWithoutZeros = formatList(
        asEntries
          .filter(([, { count }]) => count > 0)
          .map(([, { text }]) => text)
      );

      const total =
        resources.coverages +
        resources.subCoverages +
        resources.endorsements +
        resources.excessTowerLayerParticipants +
        resources.excessTowerLayers +
        resources.subjectivities;

      return {
        ...resources,
        total,
        asText,
        asTextWithoutZeros,
        hasQuoteChanged: hasChanged || total > 0,
        submission,
      };
    }
  );
};

export const useRaterIsExcessTabDisabled = () => {
  return useRaterStateContextSelector(
    ({
      quote: { coverages, endorsements, subjectivities },
      tab,
      lastEditedTab,
    }) => {
      if (tab === RaterTab.ExcessRating) {
        return false;
      }
      // Don't lock Excess for edits that originated in the Excess tab itself:
      // excess edits re-rate and mark top-level coverages hasChanged as a side
      // effect, which would otherwise trip this lock when the user navigates
      // away. The lock is meant to guard against UNSAVED edits from OTHER tabs
      // (primary/enterprise) that the excess rating depends on (CON-3364).
      if (lastEditedTab === RaterTab.ExcessRating) {
        return false;
      }
      return [
        ...coverages,
        ...coverages.reduce(
          (acc, coverage): IUnderwritingSubCoverage[] => [
            ...acc,
            ...coverage.subCoverages,
          ],
          [] as IUnderwritingSubCoverage[]
        ),
        ...endorsements,
        ...subjectivities,
      ].some(({ hasChanged }) => hasChanged);
    }
  );
};

export const useRaterIsEditable = () => {
  return useRaterStateContextSelector(
    ({
      isRequestingRatingUpdate,
      upsertQuoteIsLoading,
      isEditable,
      isRequestingUpsertQuoteNow,
      isRequestingUpsertQuoteAfterRating,
      isAuthenticated,
    }) => {
      return (
        isEditable &&
        isAuthenticated &&
        !upsertQuoteIsLoading &&
        !(isRequestingUpsertQuoteAfterRating || isRequestingUpsertQuoteNow) &&
        !isRequestingRatingUpdate
      );
    }
  );
};

export const useRaterDispatchContext = () => {
  const context = useContext(RaterDispatchContext);

  if (!context) {
    throw new Error('RaterDispatchContext not found');
  }

  return context;
};

export const useRaterActionsContext = () => {
  const context = useContext(RaterActionsContext);

  if (!context) {
    throw new Error('RaterActionsContext not found');
  }

  return context;
};

export const useRaterActionDispatchContext = () => {
  const dispatch = useRaterDispatchContext();
  const actions = useRaterActionsContext();
  return {
    dispatch,
    actions,
  };
};
